import React from "react";
import { useTranslation } from "react-i18next";
import LegalLayout from "../components/LegalLayout";
import { EMPRESA } from "../legal/empresa";

const TITULOS = { pt: "Política de Cookies", en: "Cookie Policy", es: "Política de Cookies" };
const ATUALIZADO = { pt: "18 de agosto de 2026", en: "August 18, 2026", es: "18 de agosto de 2026" };

export default function PoliticaCookies() {
  const { i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || "pt").slice(0, 2);

  const CONTEUDO = {
    pt: (
      <>
        <p>
          Esta página explica quais cookies e tecnologias semelhantes o {EMPRESA.nomeFantasia}
          {" "}utiliza em seu site institucional e painel, e como você pode gerenciá-los.
        </p>

        <h2>1. O que são cookies</h2>
        <p>Cookies são pequenos arquivos gravados no seu navegador que permitem lembrar
          preferências e reconhecer seu acesso em visitas futuras.</p>

        <h2>2. Cookies que utilizamos</h2>
        <p><strong>a) Estritamente necessários</strong> — garantem o funcionamento básico do
          sistema e não podem ser desativados:</p>
        <ul>
          <li>Token de autenticação (mantém você conectado ao painel após o login);</li>
          <li>Preferência de idioma (armazenada localmente como "saasgl_idioma", para lembrar
            se você escolheu Português, English ou Español).</li>
        </ul>
        <p><strong>b) Analíticos e de publicidade</strong> — usados apenas no site
          institucional (landing page), não dentro do painel logado:</p>
        <ul>
          <li>Google Tag Manager (GTM) — carrega tags de mensuração como Google Ads e/ou
            Google Analytics, usadas para entender a origem das visitas e medir o resultado
            de nossas campanhas de divulgação.</li>
        </ul>

        <h2>3. Consentimento</h2>
        <p>Os cookies estritamente necessários são aplicados automaticamente, pois sem eles o
          sistema não funciona. Os cookies analíticos/publicitários só são utilizados com o
          seu consentimento, dado ao continuar navegando no site institucional ou por meio de
          banner de consentimento, quando disponível. Você pode retirar esse consentimento a
          qualquer momento pelas configurações do seu navegador.</p>

        <h2>4. Como gerenciar ou desativar cookies</h2>
        <p>Você pode bloquear ou excluir cookies diretamente nas configurações do seu
          navegador. Para os cookies de publicidade do Google, também é possível ajustar
          preferências em <a href="https://myadcenter.google.com/" target="_blank" rel="noreferrer">myadcenter.google.com</a>.
          Note que bloquear o cookie de autenticação impede o funcionamento do painel.</p>

        <h2>5. Retenção</h2>
        <p>O cookie de autenticação expira automaticamente após o tempo de sessão configurado
          no sistema. A preferência de idioma permanece salva até que você a altere ou limpe
          os dados do navegador. Cookies do Google seguem o prazo definido pelo próprio Google.</p>

        <h2>6. Alterações desta política</h2>
        <p>Podemos atualizar esta Política de Cookies conforme novas ferramentas sejam
          adotadas. A data no topo desta página indica a versão vigente.</p>

        <h2>7. Contato</h2>
        <p><a href={`mailto:${EMPRESA.contatoEmail}`}>{EMPRESA.contatoEmail}</a></p>
      </>
    ),
    en: (
      <>
        <p>
          This page explains which cookies and similar technologies {EMPRESA.nomeFantasia}{" "}
          uses on its institutional website and panel, and how you can manage them.
        </p>

        <h2>1. What cookies are</h2>
        <p>Cookies are small files stored in your browser that allow us to remember
          preferences and recognize your access on future visits.</p>

        <h2>2. Cookies we use</h2>
        <p><strong>a) Strictly necessary</strong> — ensure the basic functioning of the
          system and cannot be disabled:</p>
        <ul>
          <li>Authentication token (keeps you logged in to the panel after signing in);</li>
          <li>Language preference (stored locally as "saasgl_idioma", to remember whether
            you chose Portuguese, English, or Spanish).</li>
        </ul>
        <p><strong>b) Analytics and advertising</strong> — used only on the institutional
          website (landing page), not within the logged-in panel:</p>
        <ul>
          <li>Google Tag Manager (GTM) — loads measurement tags such as Google Ads and/or
            Google Analytics, used to understand where visits come from and measure the
            results of our marketing campaigns.</li>
        </ul>

        <h2>3. Consent</h2>
        <p>Strictly necessary cookies are applied automatically, since the system cannot
          function without them. Analytics/advertising cookies are only used with your
          consent, given by continuing to browse the institutional website or through a
          consent banner, where available. You may withdraw this consent at any time
          through your browser settings.</p>

        <h2>4. How to manage or disable cookies</h2>
        <p>You can block or delete cookies directly in your browser settings. For Google's
          advertising cookies, you can also adjust your preferences at{" "}
          <a href="https://myadcenter.google.com/" target="_blank" rel="noreferrer">myadcenter.google.com</a>.
          Note that blocking the authentication cookie prevents the panel from working.</p>

        <h2>5. Retention</h2>
        <p>The authentication cookie automatically expires after the session timeout
          configured in the system. The language preference remains saved until you change
          it or clear your browser data. Google's cookies follow the retention period set
          by Google itself.</p>

        <h2>6. Changes to this policy</h2>
        <p>We may update this Cookie Policy as new tools are adopted. The date at the top
          of this page indicates the current version.</p>

        <h2>7. Contact</h2>
        <p><a href={`mailto:${EMPRESA.contatoEmail}`}>{EMPRESA.contatoEmail}</a></p>
      </>
    ),
    es: (
      <>
        <p>
          Esta página explica qué cookies y tecnologías similares utiliza{" "}
          {EMPRESA.nomeFantasia} en su sitio institucional y panel, y cómo puede
          gestionarlas.
        </p>

        <h2>1. Qué son las cookies</h2>
        <p>Las cookies son pequeños archivos guardados en su navegador que permiten
          recordar preferencias y reconocer su acceso en futuras visitas.</p>

        <h2>2. Cookies que utilizamos</h2>
        <p><strong>a) Estrictamente necesarias</strong> — garantizan el funcionamiento
          básico del sistema y no pueden desactivarse:</p>
        <ul>
          <li>Token de autenticación (lo mantiene conectado al panel después de iniciar
            sesión);</li>
          <li>Preferencia de idioma (almacenada localmente como "saasgl_idioma", para
            recordar si eligió portugués, inglés o español).</li>
        </ul>
        <p><strong>b) Analíticas y de publicidad</strong> — usadas solo en el sitio
          institucional (landing page), no dentro del panel con sesión iniciada:</p>
        <ul>
          <li>Google Tag Manager (GTM) — carga etiquetas de medición como Google Ads y/o
            Google Analytics, usadas para entender el origen de las visitas y medir el
            resultado de nuestras campañas de difusión.</li>
        </ul>

        <h2>3. Consentimiento</h2>
        <p>Las cookies estrictamente necesarias se aplican automáticamente, ya que sin
          ellas el sistema no funciona. Las cookies analíticas/publicitarias solo se
          utilizan con su consentimiento, otorgado al continuar navegando en el sitio
          institucional o mediante un banner de consentimiento, cuando esté disponible.
          Puede retirar ese consentimiento en cualquier momento desde la configuración de
          su navegador.</p>

        <h2>4. Cómo gestionar o desactivar las cookies</h2>
        <p>Puede bloquear o eliminar cookies directamente en la configuración de su
          navegador. Para las cookies de publicidad de Google, también puede ajustar sus
          preferencias en{" "}
          <a href="https://myadcenter.google.com/" target="_blank" rel="noreferrer">myadcenter.google.com</a>.
          Tenga en cuenta que bloquear la cookie de autenticación impide el funcionamiento
          del panel.</p>

        <h2>5. Retención</h2>
        <p>La cookie de autenticación expira automáticamente después del tiempo de sesión
          configurado en el sistema. La preferencia de idioma permanece guardada hasta que
          la modifique o borre los datos del navegador. Las cookies de Google siguen el
          plazo definido por el propio Google.</p>

        <h2>6. Cambios en esta política</h2>
        <p>Podemos actualizar esta Política de Cookies a medida que se adopten nuevas
          herramientas. La fecha en la parte superior de esta página indica la versión
          vigente.</p>

        <h2>7. Contacto</h2>
        <p><a href={`mailto:${EMPRESA.contatoEmail}`}>{EMPRESA.contatoEmail}</a></p>
      </>
    ),
  };

  return (
    <LegalLayout title={TITULOS[lang] || TITULOS.pt} updatedAt={ATUALIZADO[lang] || ATUALIZADO.pt}>
      {CONTEUDO[lang] || CONTEUDO.pt}
    </LegalLayout>
  );
}
